import { ColDef, ColGroupDef, ValueSetterParams } from "ag-grid-enterprise";
import {
	ChemistryElement,
	ChemistryRangeRowDraft,
	LimitDraft,
} from "../../../models/ChemistryRange";

const parseLimit = (value: any): number | null => {
	if (value === null || value === undefined) return null;
	const text = String(value).trim();
	if (text === "") return null;
	const parsed = Number(text);
	return Number.isNaN(parsed) ? null : parsed;
};

export const formatLimit = (limit: LimitDraft | undefined): string => {
	const min = limit?.MinValue ?? null;
	const max = limit?.MaxValue ?? null;
	if (min === null && max === null) return "-";
	if (min !== null && max !== null) return `${min}–${max}`;
	if (max !== null) return `≤ ${max}`;
	return `≥ ${min}`;
};

const numericCellStyle = {
	display: "flex",
	alignItems: "center",
	justifyContent: "flex-end",
	fontVariantNumeric: "tabular-nums",
};

type Bound = "MinValue" | "MaxValue";

const isInverted = (limit: LimitDraft | undefined) =>
	limit?.MinValue != null &&
	limit?.MaxValue != null &&
	limit.MinValue > limit.MaxValue;

const rangeColumn = (
	element: ChemistryElement,
): ColDef<ChemistryRangeRowDraft> => ({
	colId: element.ElementName,
	headerName: element.ElementName,
	headerTooltip: element.Description
		? `${element.ElementName} - ${element.Description}`
		: element.ElementName,
	headerClass: "ag-right-aligned-header",
	width: 104,
	flex: 0,
	sortable: false,
	editable: false,
	cellStyle: numericCellStyle,
	valueGetter: (params) =>
		formatLimit(params.data?.Limits?.[element.ElementName]),
});

const boundColumn = (
	element: ChemistryElement,
	bound: Bound,
): ColDef<ChemistryRangeRowDraft> => ({
	colId: `${element.ElementName}.${bound}`,
	headerName: bound === "MinValue" ? "Min" : "Max",
	headerTooltip: `${element.ElementName} ${
		bound === "MinValue" ? "minimum" : "maximum"
	}${element.Unit ? ` (${element.Unit})` : ""}`,
	headerClass: "ag-right-aligned-header",
	width: 72,
	flex: 0,
	sortable: false,
	editable: true,
	cellStyle: numericCellStyle,
	valueGetter: (params) =>
		params.data?.Limits?.[element.ElementName]?.[bound] ?? null,
	valueSetter: (params: ValueSetterParams<ChemistryRangeRowDraft>) => {
		const parsed = parseLimit(params.newValue);
		if (!params.data.Limits) params.data.Limits = {};
		const limits = params.data.Limits;
		const current = limits[element.ElementName] ?? {
			MinValue: null,
			MaxValue: null,
		};
		if (current[bound] === parsed) return false;
		limits[element.ElementName] = { ...current, [bound]: parsed };
		return true;
	},
	cellClass: (params) =>
		isInverted(params.data?.Limits?.[element.ElementName])
			? "chemistry-limit-invalid"
			: "",
});

const elementColumns = (
	elements: ChemistryElement[],
	isEditMode: boolean,
): (ColDef<ChemistryRangeRowDraft> | ColGroupDef<ChemistryRangeRowDraft>)[] => {
	if (!isEditMode) return elements.map(rangeColumn);

	return elements.map((element) => ({
		headerName: element.ElementName,
		headerClass: "ag-center-aligned-header",
		marryChildren: true,
		children: [
			boundColumn(element, "MinValue"),
			boundColumn(element, "MaxValue"),
		],
	}));
};

type Props = {
	isEditMode: boolean;
	elements: ChemistryElement[];
};

export const useTableData = ({ isEditMode, elements }: Props) => {
	const headerColumns: ColDef<ChemistryRangeRowDraft>[] = [
		{
			field: "SWV",
			headerName: "SWV",
			headerClass: "ag-right-aligned-header",
			width: 80,
			flex: 0,
			pinned: "left",
			editable: false,
			cellStyle: numericCellStyle,
		},
		{
			field: "GradeDescription",
			headerName: "Grade",
			width: 180,
			flex: 0,
			pinned: "left",
			sortable: false,
			editable: isEditMode,
			cellStyle: { justifyContent: "flex-start" },
		},
		{
			field: "Customer",
			headerName: "Customer",
			width: 200,
			flex: 0,
			pinned: "left",
			sortable: false,
			editable: isEditMode,
			cellStyle: { justifyContent: "flex-start" },
		},
		{
			field: "COP",
			headerName: "COP",
			headerClass: "ag-right-aligned-header",
			width: 80,
			flex: 0,
			pinned: "left",
			sortable: false,
			editable: isEditMode,
			cellStyle: numericCellStyle,
			valueParser: (params) => parseLimit(params.newValue),
		},
	];

	const trailingColumns: ColDef<ChemistryRangeRowDraft>[] = [
		{
			field: "Additions",
			headerName: "Additions",
			width: 400,
			minWidth: 320,
			flex: 1,
			sortable: false,
			editable: isEditMode,
			cellEditor: "agLargeTextCellEditor",
			cellEditorPopup: true,
			cellEditorParams: { maxLength: 200, rows: 4, cols: 40 },
			cellStyle: { justifyContent: "flex-start" },
		},
	];

	const columnDefs: (
		| ColDef<ChemistryRangeRowDraft>
		| ColGroupDef<ChemistryRangeRowDraft>
	)[] = [
		...headerColumns,
		...elementColumns(elements, isEditMode),
		...trailingColumns,
	];

	return { columnDefs };
};
